import { useEffect, useMemo, useState } from 'react';

// Our own player page (public/casino-player.html, same as the player site) on the
// H5Live host — the old stream-s-43.xink.site page no longer answers (2026-10-19).
// BASE_URL: the admin is served under /admin/ (vite base), so its public files are too.
const STREAM_BASE = import.meta.env.VITE_CASINO_STREAM_BASE || `${import.meta.env.BASE_URL}casino-player.html?id=`;

/**
 * True once the boot splash has lifted. On a hard refresh <body> carries
 * `app-loading` (main.jsx drops it ~2s later), which hides the app, so a stream
 * <iframe> made then gets a 0x0 box: the player sizes its video off that empty
 * viewport and stays zoomed / mispositioned (user 2026-10-19, mobile refresh).
 * Same fix as the player site's CasinoVideo useShellVisible.
 */
function useShellVisible() {
  const [visible, setVisible] = useState(
    () => typeof document === 'undefined' || !document.body.classList.contains('app-loading')
  );

  useEffect(() => {
    if (visible) return undefined;
    if (!document.body.classList.contains('app-loading')) {
      setVisible(true);
      return undefined;
    }
    const obs = new MutationObserver(() => {
      if (!document.body.classList.contains('app-loading')) {
        obs.disconnect();
        setVisible(true);
      }
    });
    obs.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    // Never leave the stream unmounted if the class removal is missed.
    const fallback = setTimeout(() => { obs.disconnect(); setVisible(true); }, 5000);
    return () => { obs.disconnect(); clearTimeout(fallback); };
  }, [visible]);

  return visible;
}

/**
 * Returns the casino stream URL for a given game type — empty until the app is
 * on screen, so every page's `streamUrl && <iframe>` waits for the splash.
 */
export default function useCasinoStream(gameType) {
  const shellVisible = useShellVisible();
  const streamUrl = useMemo(
    () => (shellVisible ? `${STREAM_BASE}${gameType}&v=5` : ''),
    [gameType, shellVisible]
  );
  return { streamUrl };
}
